import React, { useRef, useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { Btn, fmtDate } from '../../components/ui.jsx';
import {
  amountInWords, fmtNum, computeInvoiceTaxFromCharges, projectedTaxableCharge,
  groupChargesForInvoice, buildProjectedLineItems, DEFAULT_PROFORMA_TERMS,
} from '../../utils/invoiceUtils.js';
import { openPrintWindow, resolveAssetUrl } from '../../utils/printWindow.js';
import { fetchCorporateAccount, fetchReservationSessionServices } from '../../services/api.js';

/**
 * Formal company invoice — printable from a reservation as soon as it's
 * confirmed (before any payment), and from the Smart Folio at any later
 * point in the stay. Modelled directly on the uploaded sample (CLIENT /
 * REASON block, tabular guest charges, VAT + Tourism Tax breakdown, amount
 * in words, and a pre-configured bank/TIN footer).
 *
 * Every business-specific element — logo, TIN, VAT rate, Tourism Tax rate,
 * bank/footer text — comes from `property`, configured once in
 * Property Setup → Invoicing & Tax. Nothing here is hard-coded per business.
 */
export default function InvoicePrint({ folio, reservation, property, onClose }) {
  const printRef = useRef();

  // When this is a Corporate Reservation, the company's contract may specify
  // its own Terms & Conditions wording — resolved lazily here so the regular
  // (personal) invoice path never pays for this extra request.
  const isCorporate = reservation?.reservationCategory === 'corporate' && reservation?.corporateAccountId;
  const [corporateAccount,  setCorporateAccount]  = useState(null);
  const [sessionServices,   setSessionServices]   = useState(null);   // null = not yet loaded

  useEffect(() => {
    if (!isCorporate) { setCorporateAccount(null); setSessionServices([]); return; }
    fetchCorporateAccount(reservation.corporateAccountId).then(setCorporateAccount).catch(() => {});
    // Fetch session planned services so the corporate invoice shows
    // qty=1 | Days=planned_qty | Unit Price — instead of grouping folio charges
    fetchReservationSessionServices(reservation.id)
      .then(svcs => setSessionServices(svcs || []))
      .catch(() => setSessionServices([]));
  }, [isCorporate, reservation?.corporateAccountId, reservation?.id]);

  // Resolution order: company-specific override > property default > built-in fallback.
  const proformaTerms = corporateAccount?.proformaTermsOverride
    || property?.proformaTermsText
    || DEFAULT_PROFORMA_TERMS;

  const currency  = folio?.currency || reservation?.currency || property?.currency || 'USD';
  const vatRate       = Number(property?.vatRate       ?? 18);
  const tourismRate   = Number(property?.tourismTaxRate ?? 3);
  const roomTypeName = reservation?.roomTypeName || folio?.roomTypeName || reservation?.roomType;
  const roomNumber   = reservation?.roomNumber   || folio?.roomNumber;
  const guestName    = reservation?.guestName    || folio?.guestName || '—';
  const confirmationNumber = reservation?.confirmationNumber || folio?.confirmationNumber || '—';

  // Real folio charges if any exist yet; otherwise a projected accommodation
  // line from the reservation's own rate — so the button is useful the
  // moment a reservation is confirmed, not just after check-in.
  const realCharges = (folio?.charges || []).filter(c => !c.voided);
  // Prefer folio.numNights: it's fetched live from the folio endpoint at
  // print time. reservation.numNights can go stale after a stay amendment
  // (e.g. extend / late-checkout changes departureDate without recomputing
  // numNights on the reservation record itself), which previously caused
  // the invoice to under-count nights even though the charge total was
  // still correct (the total doesn't depend on numNights at all).
  const lineItems = realCharges.length
    ? groupChargesForInvoice(folio.charges, roomTypeName, folio?.numNights ?? reservation?.numNights)
    : buildProjectedLineItems(reservation);
  const isProjected = realCharges.length === 0;

  // Tax is summed from each charge's own server-computed taxAmount (category-based:
  // Accommodation vs Other Services), not reverse-engineered from the gross total —
  // see computeInvoiceTaxFromCharges for why that distinction matters once a stay
  // mixes room charges (3%) with other services (18%).
  const taxableCharges = isProjected ? projectedTaxableCharge(reservation, property) : realCharges;
  const tax = computeInvoiceTaxFromCharges(taxableCharges, property);
  const words = amountInWords(tax.grossTotal, currency);

  const allRoom = lineItems.length > 0 && lineItems.every(i => i.isRoom);
  const someRoom = lineItems.some(i => i.isRoom);
  const reason = allRoom ? 'ACCOMMODATION' : someRoom ? 'ACCOMMODATION & SERVICES' : 'HOTEL SERVICES';

  const today = new Date();
  const todayLong = today.toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
  const hasFooterConfig = !!(property?.tin || property?.invoiceFooterText);

  const doPrint = () => {
    const ok = openPrintWindow({
      title: `Invoice — ${confirmationNumber}`,
      bodyHtml: printRef.current.innerHTML,
      format: 'letter',
    });
    if (!ok) alert('Your browser blocked the print popup. Please allow popups for this site and try again.');
  };

  /* ── shared style tokens ── */
  const cellLabel = {
    border:`1px solid ${T.border}`, padding:'8px 12px', fontSize:11.5,
    fontWeight:800, color:T.navy, background:T.surface, width:160, whiteSpace:'nowrap',
  };
  const cellValue = {
    border:`1px solid ${T.border}`, padding:'8px 12px', fontSize:11.5, fontWeight:700, color:T.slate,
  };
  const thStyle = {
    border:`1px solid ${T.border}`, padding:'8px 10px', fontSize:10, fontWeight:800,
    color:T.white, background:T.navy, letterSpacing:.6, textTransform:'uppercase',
  };
  const tdStyle = { border:`1px solid ${T.border}`, padding:'8px 10px', fontSize:11.5, color:T.navy };
  const sumLabel = { padding:'6px 12px', fontSize:11.5, fontWeight:700, color:T.slate, textAlign:'left' };
  const sumValue = { padding:'6px 12px', fontSize:11.5, fontWeight:800, color:T.navy, textAlign:'right' };

  return (
    <div>
      {isProjected && lineItems.length === 0 && (
        <div style={{ background:T.warningPale, border:`1px solid ${T.warning}30`, borderRadius:10,
          padding:'10px 14px', marginBottom:14 }}>
          <p style={{ fontSize:12, color:T.warning, fontWeight:600 }}>
            ⚠️ No rate or charges found for this reservation yet, so the guest-charges table will be empty.
          </p>
        </div>
      )}
      {!hasFooterConfig && (
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10,
          padding:'10px 14px', marginBottom:14 }}>
          <p style={{ fontSize:12, color:T.muted }}>
            ℹ️ This property hasn't set a TIN or bank/footer details yet. Add them in{' '}
            <strong style={{ color:T.navy }}>Property Setup → Invoicing &amp; Tax</strong> so every invoice includes them automatically.
          </p>
        </div>
      )}

      {/* ── Screen / print preview ── */}
      <div ref={printRef} style={{
        fontFamily:FONTS.body, color:T.navy, background:T.white,
        maxWidth:720, margin:'0 auto', padding:'28px 30px',
        border:`1px solid ${T.border}`, borderRadius:8, boxShadow:SHADOW.lg,
      }}>

        {/* Header: property info + logo */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:18, marginBottom:18 }}>
          <div>
            <p style={{ fontFamily:FONTS.display, fontSize:19, fontWeight:700, color:T.navy, marginBottom:3 }}>
              {property?.name || 'Property Name'}
            </p>
            {property?.type && (
              <p style={{ fontSize:10.5, fontWeight:700, color:T.teal, letterSpacing:.6, textTransform:'uppercase', marginBottom:4 }}>
                {property.type}
              </p>
            )}
            {property?.addressLine1 && <p style={{ fontSize:11, color:T.slate, lineHeight:1.5 }}>{property.addressLine1}</p>}
            {property?.addressLine2 && <p style={{ fontSize:11, color:T.slate, lineHeight:1.5 }}>{property.addressLine2}</p>}
            <p style={{ fontSize:11, color:T.slate, lineHeight:1.5 }}>
              {[property?.city, property?.country].filter(Boolean).join(', ')}
            </p>
            {property?.phone && <p style={{ fontSize:11, color:T.slate, lineHeight:1.5 }}>Tel: {property.phone}</p>}
            {property?.email && <p style={{ fontSize:11, color:T.slate, lineHeight:1.5 }}>{property.email}</p>}
            {property?.tin && (
              <p style={{ fontSize:11, fontWeight:800, color:T.navy, marginTop:5 }}>
                TIN &amp; TVA: {property.tin}
              </p>
            )}
          </div>
          {property?.logoUrl && (
            <img src={resolveAssetUrl(property.logoUrl)} alt="" crossOrigin="anonymous"
              style={{ maxWidth:120, maxHeight:90, objectFit:'contain' }} />
          )}
        </div>

        {/* Title */}
        <div style={{ textAlign:'center', margin:'4px 0 18px' }}>
          <p style={{
            fontFamily:FONTS.display, fontSize:17, fontWeight:700, color:T.navy,
            textDecoration:'underline', letterSpacing:1.2,
          }}>
            INVOICE N° {confirmationNumber}
          </p>
        </div>

        {/* Client / Reason / Stay details */}
        <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:18 }}>
          <tbody>
            <tr><td style={cellLabel}>CLIENT</td><td style={cellValue}>{guestName}</td></tr>
            <tr><td style={cellLabel}>REASON</td><td style={cellValue}>{reason}</td></tr>
            <tr><td style={cellLabel}>Invoice Date</td><td style={cellValue}>{todayLong}</td></tr>
            <tr><td style={cellLabel}>Check-in Date</td><td style={cellValue}>{fmtDate(reservation?.arrivalDate)}</td></tr>
            <tr><td style={cellLabel}>Check-out Date</td><td style={cellValue}>{fmtDate(reservation?.departureDate)}</td></tr>
            <tr>
              <td style={cellLabel}>Room Number</td>
              <td style={cellValue}>
                {roomNumber ? `Room ${roomNumber}` : '—'}{roomTypeName ? ` — ${roomTypeName}` : ''}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Guest Charges ────────────────────────────────────────────────── */}
        <p style={{ fontSize:10.5, fontWeight:800, color:T.navy, letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>
          Guest Charges
        </p>

        {isCorporate && sessionServices !== null && sessionServices.length > 0 ? (
          /* ── CORPORATE: 6-column table using session planned services ──────
             qty=1 (person) | Days=planned qty | Unit Price | Tax | Total
             Accommodation still comes from folio charges, services from session. */
          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:18 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign:'left', width:'38%' }}>Description</th>
                <th style={{ ...thStyle, textAlign:'right', width:'7%'  }}>qty</th>
                <th style={{ ...thStyle, textAlign:'right', width:'7%'  }}>Days</th>
                <th style={{ ...thStyle, textAlign:'right', width:'16%' }}>Unit Price ({currency})</th>
                <th style={{ ...thStyle, textAlign:'center',width:'14%' }}>Tax</th>
                <th style={{ ...thStyle, textAlign:'right', width:'18%' }}>Total ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {/* Accommodation row from folio charges */}
              {lineItems.filter(i => i.isRoom).map(item => (
                <tr key={item.key}>
                  <td style={tdStyle}>{item.label}</td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:700 }}>1</td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:700 }}>{item.quantity}</td>
                  <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(item.unitPrice)}</td>
                  <td style={{ ...tdStyle, textAlign:'center', fontSize:9, color:T.muted, lineHeight:1.4 }}>
                    {tourismRate}% Tourism{'\n'}+ {vatRate}% VAT
                  </td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:800 }}>{fmtNum(item.total)}</td>
                </tr>
              ))}
              {/* Service rows from SessionPlannedService — qty=1 person, Days=planned qty */}
              {sessionServices.map((svc, i) => (
                <tr key={`svc-${i}`}>
                  <td style={tdStyle}>{svc.serviceLabel || svc.serviceType}</td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:700 }}>1</td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:700 }}>{Number(svc.quantity)}</td>
                  <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(Number(svc.unitPrice))}</td>
                  <td style={{ ...tdStyle, textAlign:'center', fontSize:9, color:T.muted }}>
                    {vatRate}% VAT
                  </td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:800 }}>{fmtNum(Number(svc.plannedAmount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* ── NON-CORPORATE (or corporate with no session data): original 4-col ── */
          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:18 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign:'left' }}>Description</th>
                <th style={{ ...thStyle, textAlign:'center', width:90 }}>Quantity</th>
                <th style={{ ...thStyle, textAlign:'right', width:130 }}>Unit Price ({currency})</th>
                <th style={{ ...thStyle, textAlign:'right', width:130 }}>Total ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr><td colSpan={4} style={{ ...tdStyle, textAlign:'center', color:T.muted }}>No charges yet</td></tr>
              ) : lineItems.map(item => (
                <tr key={item.key}>
                  <td style={tdStyle}>{item.label}</td>
                  <td style={{ ...tdStyle, textAlign:'center' }}>
                    {item.unit ? `${item.quantity} ${item.unit}${item.quantity > 1 ? 's' : ''}` : item.quantity}
                  </td>
                  <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(item.unitPrice)}</td>
                  <td style={{ ...tdStyle, textAlign:'right', fontWeight:800 }}>{fmtNum(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Tax breakdown */}
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:18 }}>
          <table style={{ width:300, borderCollapse:'collapse' }}>
            <tbody>
              <tr><td style={sumLabel}>Total Sales</td><td style={sumValue}>{fmtNum(tax.netSales)}</td></tr>
              {tax.breakdown.map(b => (
                <tr key={b.code}><td style={sumLabel}>{b.label} {b.ratePct}%</td><td style={sumValue}>{fmtNum(b.tax)}</td></tr>
              ))}
              {tax.breakdown.length > 0 && (
                <tr><td style={sumLabel}>Total Tax</td><td style={sumValue}>{fmtNum(tax.totalTax)}</td></tr>
              )}
              <tr>
                <td style={{ ...sumLabel, fontWeight:800, color:T.white, background:T.navy, border:`1px solid ${T.navy}` }}>
                  Total Payment
                </td>
                <td style={{ ...sumValue, color:T.goldLight, background:T.navy, border:`1px solid ${T.navy}` }}>
                  {fmtNum(tax.grossTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount in words */}
        <div style={{ borderTop:`1px dashed ${T.border}`, paddingTop:10, marginBottom:20 }}>
          <p style={{ fontSize:11.5, fontStyle:'italic', color:T.slate, lineHeight:1.6 }}>
            <strong>Total payment in words:</strong> {words} ({fmtNum(tax.grossTotal)} {currency})
            {tax.totalTax > 0 ? ` with Total Tax of ${fmtNum(tax.totalTax)} ${currency} inclusive.` : '.'}
          </p>
        </div>

        {/* Done at / authorized by / signature */}
        <div style={{ marginBottom:16 }}>
          <p style={{ fontSize:11.5, color:T.slate, marginBottom:5 }}>
            Done at {property?.city || property?.name || '—'}: {todayLong}
          </p>
          <p style={{ fontSize:11.5, color:T.slate, marginBottom:5 }}>
            Authorized by: <span style={{ display:'inline-block', minWidth:200, borderBottom:`1px solid ${T.border}` }}>&nbsp;</span>
          </p>
          <p style={{ fontSize:11.5, color:T.slate }}>Signature &amp; Stamp:</p>
        </div>

        {/* Bank / TIN footer — pre-configured per business */}
        {property?.invoiceFooterText && (
          <div style={{ borderTop:`2px solid ${T.navy}`, paddingTop:12, marginTop:10 }}>
            <p style={{ fontSize:10.5, fontWeight:700, color:T.navy, whiteSpace:'pre-wrap', lineHeight:1.7 }}>
              {property.invoiceFooterText}
            </p>
          </div>
        )}
      </div>{/* /printRef */}

      <div style={{ display:'flex', gap:10, marginTop:20, maxWidth:720, margin:'20px auto 0' }}>
        <Btn variant="secondary" onClick={onClose} style={{ flex:1 }}>Close</Btn>
        <Btn variant="primary" onClick={doPrint} style={{ flex:2 }}>🖨️ Print Invoice</Btn>
      </div>
    </div>
  );
}